import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../lib/icons';

/**
 * Click-to-zoom lightbox for images embedded in rendered rich text (ticket
 * descriptions/comments, test proof, docs) — like Jira's screenshot preview, so
 * you no longer have to open an image in a new tab to read it.
 *
 * Read-only rich content is injected via `dangerouslySetInnerHTML` into `.rich`
 * containers, so those `<img>`s aren't React-owned — we catch them with one
 * delegated document click listener instead of per-image handlers. The editable
 * TipTap surface is also `.rich` but carries `.ProseMirror`; we skip it so
 * clicking an image there still selects/resizes it. Mounted once in AppShell.
 */
export function ImageLightbox() {
  const [shot, setShot] = useState<{ src: string; alt: string } | null>(null);
  // Toggle between fit-to-screen (default) and the image's actual pixels.
  const [zoomed, setZoomed] = useState(false);
  const close = useCallback(() => { setShot(null); setZoomed(false); }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      // Leave modified / non-primary clicks alone (new tab, etc.).
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const img = (e.target as HTMLElement | null)?.closest?.('img') as HTMLImageElement | null;
      if (!img) return;
      if (!img.closest('.rich')) return;        // only rendered rich content
      if (img.closest('.ProseMirror')) return;  // not the editable editor (resize there)
      if (img.closest('a')) return;             // linked images follow their link
      e.preventDefault();
      setShot({ src: img.currentSrc || img.src, alt: img.alt || '' });
      setZoomed(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // While open: Esc closes and the background is scroll-locked.
  useEffect(() => {
    if (!shot) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [shot, close]);

  if (!shot) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label={shot.alt || 'Image preview'}>
      {/* Backdrop scrolls when the image is zoomed past the viewport; click to close. */}
      <div className="absolute inset-0 overflow-auto bg-black/80 backdrop-blur-sm" onClick={close}>
        <div className="flex min-h-full min-w-full p-4">
          <img
            src={shot.src}
            alt={shot.alt}
            onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
            className={`m-auto rounded shadow-2xl ${zoomed ? 'max-w-none cursor-zoom-out' : 'max-h-[88vh] max-w-[92vw] object-contain cursor-zoom-in'}`}
          />
        </div>
      </div>

      {/* Fixed top bar (stays put while the backdrop scrolls). */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between gap-3 p-3">
        <span className="pointer-events-auto max-w-[70%] truncate text-sm text-white/70">{shot.alt}</span>
        <div className="pointer-events-auto flex shrink-0 items-center gap-1 text-white/90">
          <a
            className="rounded p-2 hover:bg-white/10"
            href={shot.src}
            target="_blank"
            rel="noreferrer"
            title="Open original in a new tab"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon name="external" />
          </a>
          <button className="rounded p-2 hover:bg-white/10" title="Close (Esc)" onClick={close}>
            <Icon name="close" />
          </button>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-white/50">
        Click image to {zoomed ? 'fit' : 'zoom to actual size'} · Esc to close
      </div>
    </div>,
    document.body,
  );
}
